import { ago, DAY, HOUR, iso, NOW_MS, PERIOD_END_MS, PERIOD_START_MS } from "../time";
import type { SeriesPoint } from "../types/common";
import type {
  ApiKey,
  AuditEvent,
  BillingPeriod,
  Invoice,
  OverviewMetrics,
  Webhook,
  WebhookDelivery,
} from "../types/platform";
import { hash, int, pick, rng } from "./random";

export const API_KEYS: ApiKey[] = [
  {
    id: "key_prod",
    name: "Production server",
    environment: "production",
    prefix: "aux0_live_7fd2",
    createdAt: ago(31, "w"),
    createdBy: "Priya Shah",
    lastUsedAt: ago(9, "s"),
  },
  {
    id: "key_prod_ro",
    name: "Lumen pilot (read-only)",
    environment: "production",
    prefix: "aux0_live_c41a",
    createdAt: ago(2, "d"),
    createdBy: "Tom Okafor",
    lastUsedAt: ago(26, "m"),
    scopes: ["chats:read", "messages:read", "accounts:read"],
  },
  {
    id: "key_sandbox",
    name: "Local development",
    environment: "sandbox",
    prefix: "aux0_test_b8e5",
    createdAt: ago(31, "w"),
    createdBy: "Priya Shah",
    lastUsedAt: ago(4, "h"),
  },
  {
    id: "key_ci",
    name: "CI",
    environment: "sandbox",
    prefix: "aux0_test_1d93",
    createdAt: ago(12, "w"),
    createdBy: "Lena Fischer",
    lastUsedAt: ago(1, "h"),
  },
  {
    id: "key_revoked",
    name: "Old worker",
    environment: "production",
    prefix: "aux0_live_90ab",
    createdAt: ago(28, "w"),
    createdBy: "Priya Shah",
    lastUsedAt: ago(7, "w"),
    revokedAt: ago(6, "w"),
  },
];

export const WEBHOOKS: Webhook[] = [
  {
    id: "whk_main",
    url: "https://api.northwind.io/hooks/aux0",
    environment: "production",
    events: [
      "email.received",
      "email.sent",
      "calendar.event_created",
      "calendar.event_updated",
      "calendar.event_cancelled",
      "account.connected",
      "account.needs_attention",
      "account.disconnected",
      "account.sync_completed",
    ],
    enabled: true,
    createdAt: ago(31, "w"),
    secretPrefix: "whsec_4Kd9",
    health: { delivered: 18_402, failed: 11, p50Ms: 84 },
  },
  {
    id: "whk_mail",
    url: "https://api.northwind.io/hooks/aux0-mail",
    environment: "production",
    events: ["email.received", "email.sent", "email.opened", "email.bounced"],
    enabled: true,
    createdAt: ago(13, "w"),
    secretPrefix: "whsec_7Pq2",
    health: { delivered: 6_118, failed: 0, p50Ms: 71 },
  },
  {
    id: "whk_crm",
    url: "https://crm-sync.northwind.io/aux0",
    environment: "production",
    events: ["email.received", "contact.created", "contact.updated"],
    enabled: true,
    createdAt: ago(5, "w"),
    secretPrefix: "whsec_2Mz8",
    health: {
      delivered: 2_904,
      failed: 388,
      failingSince: ago(9, "h"),
      p50Ms: 2_410,
    },
  },
  {
    id: "whk_sandbox",
    url: "https://northwind.ngrok.app/aux0",
    environment: "sandbox",
    events: ["email.received", "account.connected"],
    enabled: false,
    createdAt: ago(3, "w"),
    secretPrefix: "whsec_9Xf1",
    health: { delivered: 212, failed: 4, p50Ms: 132 },
  },
];

/** The payload shape every `message.received` delivery carries. */
function emailPayload(): string {
  return JSON.stringify(
    {
      id: "evt_2tKd9fQm",
      type: "email.received",
      created_at: ago(22, "m"),
      data: {
        message: {
          id: "eml_rfp_2",
          thread_id: "thr_rfp",
          account_id: "acc_gm_maya",
          provider: "gmail",
          direction: "inbound",
          subject: "Northwind — security questionnaire + DPA",
          snippet: "Legal cleared the DPA this morning. Two items left from security:",
          sent_at: ago(22, "m"),
          from: {
            name: "Devika Menon",
            address: "devika@lumenfreight.com",
          },
          attachments: [{ filename: "Lumen-Northwind-DPA-signed.pdf", size: 311296 }],
        },
        account: {
          id: "acc_gm_maya",
          provider: "gmail",
          end_user_id: "u_maya",
        },
      },
    },
    null,
    2,
  );
}

function attentionPayload(): string {
  return JSON.stringify(
    {
      id: "evt_8Lp3nBxQ",
      type: "account.needs_attention",
      created_at: ago(3, "h"),
      data: {
        account: {
          id: "acc_ic_tom",
          provider: "icloud",
          handle: "tom.okafor@icloud.com",
          end_user_id: "u_tom",
          status: "needs_attention",
        },
        attention: {
          code: "password_changed",
          message:
            "Apple revoked this app password when the Apple Account password changed.",
          self_serve: true,
          since: ago(3, "h"),
        },
        // The link to send the end user so they can clear it themselves.
        reconnect_url: "https://auth.aux0.com/r/2kd9fQmLp3",
      },
    },
    null,
    2,
  );
}

const DELIVERY_EVENTS = [
  "email.received",
  "email.sent",
  "email.opened",
  "calendar.event_created",
  "calendar.event_updated",
  "contact.updated",
  "account.connected",
] as const;

/** Recent deliveries, newest first. The failing CRM hook dominates them. */
export const DELIVERIES: WebhookDelivery[] = [
  {
    id: "dlv_1",
    webhookId: "whk_main",
    event: "email.received",
    subjectId: "eml_rfp_2",
    at: ago(22, "m"),
    status: "delivered",
    responseStatus: 200,
    attempts: 1,
    durationMs: 79,
    payload: emailPayload(),
    responseBody: '{"ok":true}',
  },
  {
    id: "dlv_2",
    webhookId: "whk_crm",
    event: "email.received",
    subjectId: "eml_rfp_2",
    at: ago(22, "m"),
    status: "failed",
    responseStatus: 504,
    attempts: 5,
    durationMs: 10_000,
    payload: emailPayload(),
    responseBody: "upstream request timeout",
  },
  {
    id: "dlv_3",
    webhookId: "whk_main",
    event: "account.needs_attention",
    subjectId: "acc_ic_tom",
    at: ago(3, "h"),
    status: "delivered",
    responseStatus: 200,
    attempts: 1,
    durationMs: 91,
    payload: attentionPayload(),
    responseBody: '{"ok":true}',
  },
  ...Array.from({ length: 40 }, (_, index) => {
    const r = rng(hash(`delivery:${index}`));
    const failing = r() < 0.17;
    const webhookId = failing ? "whk_crm" : pick(r, ["whk_main", "whk_mail"]);
    const minutesAgo = int(r, 25, 60 * 20);
    return {
      id: `dlv_${index + 4}`,
      webhookId,
      event: pick(r, DELIVERY_EVENTS),
      subjectId: `eml_${Math.floor(r() * 1e8).toString(36)}`,
      at: ago(minutesAgo, "m"),
      status: failing ? "failed" : "delivered",
      responseStatus: failing ? pick(r, [502, 504, 500]) : 200,
      attempts: failing ? 5 : 1,
      durationMs: failing ? 10_000 : int(r, 48, 260),
      payload: emailPayload(),
      responseBody: failing ? "upstream request timeout" : '{"ok":true}',
    } satisfies WebhookDelivery;
  }),
];

const AUDIT_TEMPLATES: {
  action: string;
  summary: (name: string) => string;
  actorKind: AuditEvent["actorKind"];
}[] = [
  {
    action: "account.connected",
    summary: (name) => `${name} linked a Google Calendar account through hosted auth`,
    actorKind: "system",
  },
  {
    action: "account.paused",
    summary: (name) => `${name} paused a Zoho mailbox`,
    actorKind: "user",
  },
  {
    action: "email.sent",
    summary: () => "Sent a reply on thr_rfp from maya@northwind.io",
    actorKind: "api_key",
  },
  {
    action: "webhook.updated",
    summary: (name) => `${name} added email.opened to the mail webhook`,
    actorKind: "user",
  },
  {
    action: "key.created",
    summary: (name) => `${name} created a read-only production key`,
    actorKind: "user",
  },
  {
    action: "key.revoked",
    summary: (name) => `${name} revoked the Old worker key`,
    actorKind: "user",
  },
  {
    action: "member.invited",
    summary: (name) => `${name} invited marco@northwind.io as a developer`,
    actorKind: "user",
  },
  {
    action: "send.held",
    summary: () =>
      "Held a send: acc_gm_sofia is at 1,894 of its 2,000 daily Gmail sends",
    actorKind: "system",
  },
  {
    action: "sync.reconciled",
    summary: () => "UIDVALIDITY changed on acc_im_ops; reconciled without re-download",
    actorKind: "system",
  },
];

const ACTORS = [
  "Priya Shah",
  "Tom Okafor",
  "Lena Fischer",
  "Dan Whitfield",
  "Production server",
  "Aux0",
];

export const AUDIT_EVENTS: AuditEvent[] = Array.from({ length: 60 }, (_, index) => {
  const r = rng(hash(`audit:${index}`));
  const template = pick(r, AUDIT_TEMPLATES);
  const actor =
    template.actorKind === "api_key"
      ? "Production server"
      : template.actorKind === "system"
        ? "Aux0"
        : pick(r, ACTORS.slice(0, 4));
  const minutesAgo = int(r, 2, 60 * 24 * 14);
  return {
    id: `aud_${index}`,
    at: ago(minutesAgo, "m"),
    actor,
    actorKind: template.actorKind,
    action: template.action,
    target: pick(r, [
      "account:acc_gm_maya",
      "account:acc_ic_tom",
      "webhook:whk_mail",
      "key:key_prod_ro",
      "member:marco@northwind.io",
    ]),
    summary: template.summary(actor),
    environment: r() < 0.82 ? "production" : "sandbox",
    requestId: `req_${Math.floor(r() * 1e12).toString(36)}`,
    ip:
      template.actorKind === "user"
        ? `${int(r, 20, 200)}.${int(r, 1, 250)}.•.•`
        : undefined,
  } satisfies AuditEvent;
}).sort((a, b) => Date.parse(b.at) - Date.parse(a.at));

export const METRICS: OverviewMetrics = {
  accountsConnected: 9,
  accountsNeedingAttention: 2,
  messagesSent: 2_481,
  messagesReceived: 3_714,
  emailsSent: 560,
  apiCalls: 184_022,
  errorRate: 0.0042,
  latencyP50Ms: 118,
  latencyP95Ms: 412,
};

/**
 * A metric series over `hours`, shaped with a daily working-hours curve so the
 * dashboard chart reads like real traffic rather than noise.
 */
function series(seed: string, hours: number, peak: number): SeriesPoint[] {
  const r = rng(hash(seed));
  const step = hours <= 24 ? HOUR : hours <= 24 * 7 ? 2 * HOUR : DAY;
  const buckets = Math.floor((hours * HOUR) / step);
  const start = NOW_MS - buckets * step;

  return Array.from({ length: buckets }, (_, index) => {
    const at = start + index * step;
    const hourOfDay = new Date(at).getUTCHours();
    const dayOfWeek = new Date(at).getUTCDay();
    // Working hours in Europe and the US east coast, nothing much at night.
    const daily = 0.18 + 0.82 * Math.max(0, Math.sin(((hourOfDay - 5) / 17) * Math.PI));
    const weekly = dayOfWeek === 0 || dayOfWeek === 6 ? 0.28 : 1;
    const jitter = 0.85 + r() * 0.3;
    return { at: iso(at), value: Math.round(peak * daily * weekly * jitter) };
  });
}

export const USAGE_SERIES = {
  "24h": {
    apiCalls: series("calls:24h", 24, 480),
    messages: series("msgs:24h", 24, 42),
    accounts: series("accts:24h", 24, 8),
  },
  "7d": {
    apiCalls: series("calls:7d", 24 * 7, 520),
    messages: series("msgs:7d", 24 * 7, 46),
    accounts: series("accts:7d", 24 * 7, 8),
  },
  "30d": {
    apiCalls: series("calls:30d", 24 * 30, 6_200),
    messages: series("msgs:30d", 24 * 30, 240),
    accounts: series("accts:30d", 24 * 30, 8),
  },
  "90d": {
    apiCalls: series("calls:90d", 24 * 90, 5_400),
    messages: series("msgs:90d", 24 * 90, 210),
    accounts: series("accts:90d", 24 * 90, 7),
  },
} as const;

export const BILLING: BillingPeriod = {
  planId: "growth",
  planName: "Growth",
  startsAt: iso(PERIOD_START_MS),
  endsAt: iso(PERIOD_END_MS),
  accountsIncluded: 10,
  accountsUsed: 13,
  accountOverageCents: 700,
  callsIncluded: 250_000,
  callsUsed: 184_022,
  // $99 plan + 2 accounts over at $7 = $113.
  projectedCents: 11_300,
};

export const INVOICES: Invoice[] = [
  {
    id: "inv_sep",
    number: "AUX0-2026-0912",
    periodStart: iso(Date.UTC(2026, 8, 1)),
    periodEnd: iso(Date.UTC(2026, 9, 1)),
    amountCents: 10_600,
    status: "paid",
    paidAt: iso(Date.UTC(2026, 9, 2, 9, 14)),
  },
  {
    id: "inv_aug",
    number: "AUX0-2026-0811",
    periodStart: iso(Date.UTC(2026, 7, 1)),
    periodEnd: iso(Date.UTC(2026, 8, 1)),
    amountCents: 9_900,
    status: "paid",
    paidAt: iso(Date.UTC(2026, 8, 2, 9, 11)),
  },
  {
    id: "inv_jul",
    number: "AUX0-2026-0710",
    periodStart: iso(Date.UTC(2026, 6, 1)),
    periodEnd: iso(Date.UTC(2026, 7, 1)),
    amountCents: 9_900,
    status: "paid",
    paidAt: iso(Date.UTC(2026, 7, 2, 9, 8)),
  },
];
